import * as React from 'react'

import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import CssBaseline from '@mui/material/CssBaseline'
import Application, { getRoute } from 'Application'
import {
  GlotstackProvider,
  LocaleRegion,
  LogLevel,
  setLogLevel,
  Translations,
} from 'glotstack'
import { Action } from 'history'
import { merge } from 'lodash'
import { MatchResult } from 'path-to-regexp'
import queryString, { ParsedQuery } from 'query-string'
import { hydrateRoot } from 'react-dom/client'
import { Provider } from 'react-redux'

import { getHistory } from 'domains/application/hooks'
import { DEFAULT_STATE as APPLICATION_DEFAULT_STATE } from 'domains/application/redux'
import { AppState, createStore } from 'domains/redux/store'

window.__IS_SERVER__ = false

function createEmotionCache() {
  return createCache({ key: 'css' })
}

const importMethod = async (locale: LocaleRegion) => {
  const result = await fetch(`/static/translations/${locale}.json`)
  return await result.json()
}

setLogLevel(LogLevel.INFO)

async function run() {
  const store = createStore(
    merge(
      {
        application: APPLICATION_DEFAULT_STATE,
      },
      global.__APP__.state,
    ) as Partial<AppState>,
  )

  if (__APP__.env === 'dev') {
    window.__STORE__ = store
  }

  const history = getHistory()
  window.__HISTORY__ = history

  const queryArgs = (): ParsedQuery => {
    return (
      queryString.parse(location.search, { arrayFormat: 'bracket' }) ??
      undefined
    )
  }

  // TODO: switch to history.block(...)
  history?.listen((update) => {
    const path = update.location.pathname
    const route = getRoute(update.location.pathname ?? '')
    let urlData: MatchResult<Record<string, string>> | false | null = route[0](
      location.pathname,
    )
    if (urlData === false) {
      urlData = null
    }

    store.dispatch({
      type: 'application/setPath',
      payload: {
        path,
        queryArgs: queryArgs(),
        pathArgs: urlData?.params,
        args: merge({}, urlData?.params ?? {}, queryArgs() ?? {}),
        action: Action.Push,
        clearFlash: true,
      },
    })
  })

  const route = getRoute(location.pathname ?? '')
  let urlData: MatchResult<Record<string, string>> | false | null = route[0](
    location.pathname,
  )
  if (urlData === false) {
    urlData = null
  }

  store.dispatch({
    type: 'application/setPath',
    payload: {
      path: window.location.pathname,
      queryArgs: queryArgs(),
      pathArgs: urlData?.params,
      args: merge({}, urlData?.params ?? {}, queryArgs() ?? {}),
      action: Action.Push,
      clearFlash: false,
    },
  })

  const cache = createEmotionCache()

  const handleTranslationLoaded = (
    locale: LocaleRegion,
    translations: Translations,
  ) => {
    store.dispatch({
      type: 'application/updateTranslations',
      payload: { [locale]: translations },
    })
  }

  hydrateRoot(
    document.getElementById('app')!,
    <>
      <GlotstackProvider
        initialTranslations={merge(
          {},
          store.getState().application.translations,
        )}
        initialLocale={store.getState().application.locale}
        importMethod={importMethod}
        onTranslationLoaded={handleTranslationLoaded}
      >
        <Provider store={store}>
          <CacheProvider value={cache}>
            <CssBaseline>
              <Application />
            </CssBaseline>
          </CacheProvider>
        </Provider>
      </GlotstackProvider>
    </>,
  )
}

run()
